import { useState } from 'react'
import { useParams } from 'react-router-dom-v5-compat'

import {
  Button,
  Dialog,
  Flex,
  Label,
  SimpleSpinner,
  Text,
  Textarea,
  toast,
} from '@buffer-mono/popcorn'

import { useCommentsInsights } from '../hooks/useCommentsInsights'

import styles from './insightsButton.module.css'

const PromptErrorMessage = (): JSX.Element => (
  <Text id="prompt-error" size="sm" color="critical">
    Please enter a prompt to generate insights
  </Text>
)

export const InsightsButton = (): JSX.Element => {
  const { channelId } = useParams<{ channelId: string }>()
  const { fetchCommentsInsights } = useCommentsInsights()
  const [isOpen, setIsOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [insights, setInsights] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPromptError, setShowPromptError] = useState(false)

  const handleSubmit = async (): Promise<void> => {
    if (!prompt.trim()) {
      setShowPromptError(true)
      return
    }

    if (!channelId) {
      return
    }

    setShowPromptError(false)
    setIsLoading(true)
    try {
      const result = await fetchCommentsInsights({
        channelId,
        prompt: prompt.trim(),
      })

      if (result.error) {
        toast.error('Failed to fetch insights')
        return
      }

      const insightsData = result.data?.commentsInsights?.insights
      if (insightsData) {
        setInsights(insightsData)
        toast.success('Insights generated successfully')
      }
    } catch (error) {
      toast.error('Failed to fetch insights')
      console.error('Error fetching insights:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = (): void => {
    setIsOpen(false)
    setPrompt('')
    setInsights('')
    setShowPromptError(false)
  }

  const handlePromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setPrompt(e.target.value)
    if (showPromptError && e.target.value.trim()) {
      setShowPromptError(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <Button variant="tertiary">Insights</Button>
      </Dialog.Trigger>
      <Dialog.Content size="large">
        <Dialog.Header>
          <Dialog.Title>Comments Insights</Dialog.Title>
          <Dialog.Description>
            Get AI-powered insights from your comments by providing a prompt.
            The AI will analyze your recent comments and posts to provide
            valuable insights.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body>
          <Flex className={styles.insightsContainer} direction="row" gap="lg">
            <div>
              <Label htmlFor="prompt">Prompt</Label>
              <Flex direction="column" gap="xs">
                {showPromptError && <PromptErrorMessage />}
                <Textarea
                  id="prompt"
                  placeholder="e.g., What are the main themes in our comments? What questions do our followers ask most? What sentiment do our comments have?"
                  value={prompt}
                  onChange={handlePromptChange}
                  rows={10}
                  resize="none"
                  aria-label="Enter your prompt for generating insights"
                  aria-describedby={
                    showPromptError ? 'prompt-error' : undefined
                  }
                  aria-required="true"
                />
              </Flex>
            </div>
            <div>
              <Label htmlFor="insights">Insights</Label>
              <div style={{ position: 'relative' }}>
                <Textarea
                  id="insights"
                  placeholder={
                    isLoading
                      ? 'Generating insights...'
                      : "Click 'Generate Insights' to get AI-powered analysis of your comments..."
                  }
                  value={insights}
                  readOnly
                  rows={10}
                  resize="none"
                  aria-label="Generated insights from your comments"
                />
                {isLoading && (
                  <div className={styles.spinner}>
                    <SimpleSpinner size="medium" />
                  </div>
                )}
              </div>
            </div>
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button
              variant="secondary"
              onClick={handleClose}
              aria-label="Cancel and close insights dialog"
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={isLoading || !prompt.trim()}
            aria-label={
              isLoading
                ? 'Generating insights, please wait'
                : 'Generate insights from comments'
            }
            aria-describedby={!prompt.trim() ? 'prompt-error' : undefined}
          >
            {isLoading ? 'Generating...' : 'Generate Insights'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
